'use client';

import type { ComponentType } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/cn';

export const TOPBAR_FONT_FAMILY = 'inherit';
export const TOPBAR_TEXT_BUTTON_CLASSNAME = 'relative -mx-1 flex h-8 items-center rounded-full px-2.5 text-[13px] font-medium leading-none tracking-normal transition-colors duration-150 md:h-9 focus-visible:outline-2 focus-visible:outline-[#1787B2] focus-visible:outline-offset-2 [@media(pointer:coarse)]:min-h-11';
export const TOPBAR_ICON_BUTTON_CLASSNAME = 'group relative flex h-8 w-8 items-center justify-center rounded-full transition-colors duration-150 md:h-9 md:w-9 focus-visible:outline-2 focus-visible:outline-[#1787B2] focus-visible:outline-offset-2 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11';
export const TOPBAR_ICON_CLASSNAME = 'h-[18px] w-[18px]';
export const TOPBAR_ICON_TOOLTIP_CLASSNAME = 'pointer-events-none absolute left-1/2 top-full z-[160] mt-1.5 -translate-x-1/2 whitespace-nowrap rounded-xl bg-[#203644] px-3 py-2 text-xs font-medium leading-5 text-white opacity-0 shadow-lg transition-opacity duration-75 group-hover:opacity-100 group-focus-within:opacity-100';

type TopbarIconComponent = ComponentType<{ className?: string; strokeWidth?: number }>;
type TopbarButtonTone = NonNullable<TopbarTextButtonProps['tone']>;

interface TopbarTextButtonProps {
  label: string;
  active?: boolean;
  tone?: 'neutral' | 'accent-outline' | 'accent-solid';
  icon?: TopbarIconComponent;
  showLabel?: boolean;
  className?: string;
  indicatorLayoutId?: string;
  onClick: () => void;
}

export function TopbarTextButton({
  label,
  active = false,
  tone = 'neutral',
  icon: Icon,
  showLabel = false,
  className,
  indicatorLayoutId,
  onClick,
}: TopbarTextButtonProps) {
  const reduceMotion = useReducedMotion();
  const isAccentTone = isTopbarAccentTone(tone);
  const toneClassName = getTopbarToneClassName({ active, tone });

  if (Icon && showLabel) {
    return (
      <button
        type="button"
        data-studio-control
        onClick={onClick}
        aria-pressed={active}
        style={{ fontFamily: TOPBAR_FONT_FAMILY }}
        className={cn(
          TOPBAR_TEXT_BUTTON_CLASSNAME,
          'inline-flex gap-1.5',
          toneClassName,
          getTopbarIconSurfaceClassName({ active, tone }),
          className,
        )}
      >
        <Icon className={TOPBAR_ICON_CLASSNAME} strokeWidth={2.05} aria-hidden />
        <span className="whitespace-nowrap">{label}</span>
      </button>
    );
  }

  if (Icon) {
    return (
      <button
        type="button"
        data-studio-control
        onClick={onClick}
        aria-label={label}
        aria-pressed={active}
        style={{ fontFamily: TOPBAR_FONT_FAMILY }}
        className={cn(
          TOPBAR_ICON_BUTTON_CLASSNAME,
          toneClassName,
          getTopbarIconSurfaceClassName({ active, tone }),
          className,
        )}
      >
        <Icon className={TOPBAR_ICON_CLASSNAME} strokeWidth={2.05} aria-hidden />
        <span
          className={TOPBAR_ICON_TOOLTIP_CLASSNAME}
          aria-hidden
        >
          {label}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      data-studio-control
      onClick={onClick}
      aria-pressed={active}
      style={{ fontFamily: TOPBAR_FONT_FAMILY }}
      className={cn(
        TOPBAR_TEXT_BUTTON_CLASSNAME,
        toneClassName,
        className,
      )}
    >
      <span className="whitespace-nowrap">{label}</span>
      {active && indicatorLayoutId && !reduceMotion ? (
        <motion.span
          layoutId={indicatorLayoutId}
          className={cn(
            'absolute bottom-[7px] left-0 right-0 h-px rounded-full',
            isAccentTone ? 'bg-accent-core' : 'bg-black/70',
          )}
          transition={{ type: 'spring', stiffness: 350, damping: 30 }}
        />
      ) : active ? (
        <span
          className={cn(
            'absolute bottom-[7px] left-0 right-0 h-px rounded-full',
            isAccentTone ? 'bg-accent-core' : 'bg-black/70',
          )}
        />
      ) : null}
    </button>
  );
}

function isTopbarAccentTone(tone: TopbarButtonTone) {
  return tone === 'accent-solid' || tone === 'accent-outline';
}

export function getTopbarToneClassName({
  active = false,
  tone = 'neutral',
}: {
  active?: boolean;
  tone?: TopbarButtonTone;
}) {
  if (isTopbarAccentTone(tone)) {
    return 'text-[#176B89] hover:text-[#203644]';
  }
  return active
    ? 'text-hr-text-primary'
    : 'text-hr-text-secondary hover:text-hr-text-primary';
}

export function getTopbarIconSurfaceClassName({
  active = false,
  tone = 'neutral',
}: {
  active?: boolean;
  tone?: TopbarButtonTone;
}) {
  const isAccentTone = isTopbarAccentTone(tone);
  return cn(
    isAccentTone ? 'hover:bg-accent-soft/60' : 'hover:bg-black/[0.04]',
    active && !isAccentTone && 'bg-black/[0.045]',
    active && isAccentTone && 'bg-accent-soft/70',
  );
}
